import { Injectable } from '@angular/core';
import { ILocalBranch } from '@models/localBranch';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import * as actions from './localBranches.actions';

import {
	BusinessWithSubscriptionStateModel,
	FirestoreStateBaseForBusinessWithSubscription,
} from '@state/FirestoreStateBaseForBusiness';
import { CollectionPaths } from '@models/collections';
import { createId } from '@core/util/file';
import { DEFAULT_STATE_BASE } from '@state/FirestoreStateBase';
import { MatterMortgageDispatch } from '@state/matter/matter_mortgages';
import { Funding } from '@models/mortgage';

export interface LocalBranchesStateModel
	extends BusinessWithSubscriptionStateModel {
	items: ILocalBranch[];
}

const DEFAULT_STATE: LocalBranchesStateModel = {
	...DEFAULT_STATE_BASE,
	items: [],
};
type Ctx = StateContext<LocalBranchesStateModel>;

@State<LocalBranchesStateModel>({
	name: 'localBranches',
	defaults: DEFAULT_STATE,
})
@Injectable()
export class LocalBranchesState extends FirestoreStateBaseForBusinessWithSubscription<
	ILocalBranch,
	LocalBranchesStateModel
> {
	constructor(private matterMortgageDispatch: MatterMortgageDispatch) {
		super(
			(pathParams) =>
				CollectionPaths.business_localBranches(pathParams.businessId),
			(doc) => ({
				items: doc as ILocalBranch[],
			}),
			() => DEFAULT_STATE,
		);
	}

	@Selector([LocalBranchesState])
	static getAll(state: LocalBranchesStateModel): ILocalBranch[] {
		return state.items;
	}

	@Selector([LocalBranchesState])
	static get(state: LocalBranchesStateModel): (id: string) => ILocalBranch {
		return (id: string) => state.items.find((x) => x.id === id);
	}

	@Action(actions.Create)
	async create(_ctx: Ctx, { localBranch, mortgageId }: actions.Create) {
		const id = createId();
		await this.repo.CreateOrMerge(id, localBranch);

		if (mortgageId) {
			this.matterMortgageDispatch.updateMortgage(mortgageId, {
				fundingDetails: { localBranch: localBranch } as Funding,
			});
		}

		return;
	}

	@Action(actions.Remove)
	async removeLocalBranch(
		_ctx: Ctx,
		{ localBranchId }: actions.Remove,
	): Promise<void> {
		return await this.repo.Delete(localBranchId);
	}

	@Action(actions.UpdateLocalBranch)
	async updateLocalBranch(_ctx: Ctx, { payload }: actions.UpdateLocalBranch) {
		return await this.repo.CreateOrMerge(payload.id, payload);
	}
}
